import { useEffect, useRef, useState, FC } from 'react'
import Fuse from 'fuse.js'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  SearchResult,
  useKeyPress,
  DialogQuestionIcon,
  GroupIcon,
  Text,
} from '@instabox/consumer-web-ui'
import {
  GetFaqsAndCategoriesQuery,
  getTranslation,
  useRouterEvent,
} from '@instabox/consumer-web-shared'

import { Filter } from '../components/FAQSearch/FAQFilter'
import FAQSearch from '../components/FAQSearch'

const limit = 5
const fuseKeys = ['translations.question', 'translations.name', 'question', 'name']

const createFuseInstance = collection => {
  return new Fuse(collection, { keys: fuseKeys })
}

export interface FAQSearchContainerProps {
  categories: GetFaqsAndCategoriesQuery['categories']
  faqs: GetFaqsAndCategoriesQuery['faqs']
  defaultValue?: string
}

const FAQSearchContainer: FC<FAQSearchContainerProps> = ({
  defaultValue = '',
  categories,
  faqs,
}) => {
  const { push } = useRouter()
  const { formatMessage } = useIntl()
  const [value, setValue] = useState(defaultValue)
  const [searchResults, setSearchResults] = useState([])
  const fuse = useRef(null)
  const enterKeyClicked = useKeyPress('Enter')
  const [selectedCategory, setSelectedCategory] = useState('')
  const categoryFilter: Filter = {
    label: formatMessage({ id: 'help.filter.select_category' }),
    id: 'category-filter',
    value: selectedCategory,
    options: categories.map(category => ({
      id: category.id,
      label: getTranslation('name', category.translations, category.name),
    })),
    onChange: (_, value) => setSelectedCategory(value),
  }
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    fuse.current = createFuseInstance([...categories, ...faqs])
  }, [categories, faqs])

  useRouterEvent('routeChangeStart', () => {
    resetSearchResult()
  })

  useEffect(() => {
    if (enterKeyClicked) {
      push({
        pathname: `/help/search`,
        query: {
          q: value,
        },
      })
    }
  }, [enterKeyClicked, push, value])

  useEffect(() => {
    setSearchResults(fuse.current.search(value))
  }, [value])

  const applyFilters = () => {
    const filteredCategories = categories.filter(category => category.id === selectedCategory)
    const filteredFaqs = faqs.filter(faq =>
      faq.categories.some(category => category.faq_categories_id.id === selectedCategory),
    )
    fuse.current = createFuseInstance([...filteredFaqs, ...filteredCategories])
  }

  const resetFilters = () => {
    setSelectedCategory('')
    fuse.current = createFuseInstance([...faqs, ...categories])
  }

  const resetSearchResult = () => {
    setSearchResults([])
  }

  const handleClose = () => {
    setIsFocused(false)
  }

  let children = null
  if (searchResults.length > 0) {
    children = searchResults.slice(0, limit).map(({ item, refIndex }) => {
      const translationKey = item.__typename === 'faqs' ? 'question' : 'name'
      const Icon = item.__typename === 'faqs' ? DialogQuestionIcon : GroupIcon
      const href = item.__typename === 'faqs' ? `/help/${item.slug}` : `/help/category/${item.slug}`
      const weight = item.__typename === 'faqs' ? 'regular' : 'medium'
      return (
        <SearchResult iconBefore={<Icon />} key={refIndex}>
          <Text as="span" fontWeight={weight} fontSize="sm">
            <Link href={href} className="no-underline">
              {getTranslation(translationKey, item.translations, item[translationKey])}
            </Link>
          </Text>
        </SearchResult>
      )
    })
  } else {
    children = (
      <Text align="center" fontSize="sm">
        <FormattedMessage id="help.search.no_results" values={{ query: value }} />
      </Text>
    )
  }

  return (
    <FAQSearch
      value={value}
      onValueChange={setValue}
      onClose={handleClose}
      showResults={value.length > 0 && isFocused}
      filters={[categoryFilter]}
      onFilterApply={applyFilters}
      onFilterReset={resetFilters}
      isFocused={isFocused}
      onFocus={() => setIsFocused(true)}>
      {children}
    </FAQSearch>
  )
}

export default FAQSearchContainer
